import { getApiBaseUrl } from '../lib/api';
/**
 * PropertyDetailV2 - Rightmove/Zoopla Standard Property Detail Page
 * Complete property information with media gallery, costs, and action CTAs
 * 
 * Supports two modes:
 * - Public view (tenants): Enquire, Book Viewing actions
 * - Landlord view (owner): Edit, Manage actions
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Heart, Share2, X, MapPin, Bed, Bath, 
  Sofa, Calendar, PoundSterling, Clock, Building, FileText, CheckCircle,
  Phone, Mail, ArrowLeft, Expand, Grid, AlertCircle, Edit, Settings, 
  Home, Users, FileCheck, Eye, EyeOff
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { EnquiryDialog, ViewingRequestDialog } from '../components/property';
import { cn } from '../lib/utils';

const API_URL = getApiBaseUrl();

// Placeholder images
const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&h=800&fit=crop',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200&h=800&fit=crop',
  'https://images.unsplash.com/photo-1554995207-c18c203602cb?w=1200&h=800&fit=crop',
  'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&h=800&fit=crop',
  'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=1200&h=800&fit=crop',
];

const PropertyDetailV2 = ({ mode = 'public' }) => {
  // mode: 'public' (tenant view) or 'landlord' (owner management view)
  const { id, propertyId } = useParams(); // Support both route patterns
  const actualId = id || propertyId;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const galleryRef = useRef(null);
  
  // Determine if this is landlord view based on route or prop
  const isLandlordView = mode === 'landlord' || location.pathname.includes('/landlord/');

  // Data State
  const [property, setProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  // UI State
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showViewing, setShowViewing] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  // Fetch property details
  useEffect(() => {
    const fetchProperty = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        
        // For landlord view, use the authenticated properties endpoint
        // For public view, use the search endpoint (which only returns listed properties)
        const endpoint = isLandlordView 
          ? `${API_URL}/api/properties/${actualId}`
          : `${API_URL}/api/search/properties/${actualId}`;
        
        const response = await fetch(endpoint, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Property not found');
          }
          throw new Error('Failed to load property');
        }

        const data = await response.json();
        setProperty(data);
        setIsSaved(data.is_saved || false);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (actualId) {
      fetchProperty();
    }
  }, [actualId, isLandlordView]);

  // Check if current user is the property owner
  const canManage = isLandlordView && user && property && 
    (property.landlord_id === user.id || user.role === 'landlord_admin');

  // Get images
  const media = property?.media || [];
  const images = media.length > 0
    ? media.map(m => m.url || m)
    : PLACEHOLDER_IMAGES.slice(0, 3);

  // Format price
  const formatPrice = (amount, frequency = 'pcm') => {
    if (!amount) return 'POA';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
    }).format(amount) + ` ${frequency}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Now';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Display address
  const displayAddress = property?.address
    ? `${property.address.line1 || ''}${property.address.line2 ? ', ' + property.address.line2 : ''}`
    : '';
  const locationLine = property?.address
    ? `${property.address.city || ''}, ${property.address.postcode || ''}`
    : '';

  // Save/unsave handler
  const handleSave = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/properties/${id}` } });
      return;
    }

    const token = localStorage.getItem('token');
    try {
      if (isSaved) {
        await fetch(`${API_URL}/api/search/saved-properties/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } else {
        await fetch(`${API_URL}/api/search/saved-properties`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ property_id: id }),
        });
      }
      setIsSaved(!isSaved);
    } catch (err) {
      console.error('Failed to save property:', err);
    }
  };

  // Share handler
  const handleShare = async () => {
    const url = window.location.href;
    const text = `Check out this ${property?.bedrooms} bed ${property?.property_type} - ${formatPrice(property?.rent_amount, property?.rent_frequency)}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: property?.listing_title || 'Property', text, url });
      } catch (shareError) {
        // User cancelled share or error occurred
        console.debug('Share cancelled or failed', shareError);
      }
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  // Gallery navigation
  const prevImage = () => {
    setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };

  const nextImage = () => {
    setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-32" />
            <div className="aspect-[16/9] bg-gray-200 rounded-lg" />
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                <div className="h-8 bg-gray-200 rounded w-1/2" />
                <div className="h-6 bg-gray-200 rounded w-1/3" />
                <div className="h-32 bg-gray-200 rounded" />
              </div>
              <div className="h-64 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{error}</h1>
          <p className="text-gray-600 mb-6">The property you&apos;re looking for isn&apos;t available.</p>
          <button
            onClick={() => navigate('/search')}
            className="px-6 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 md:pb-8">
      {/* Back Navigation */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => {
              // Navigate back to the appropriate list
              if (isLandlordView) {
                navigate('/landlord/properties');
              } else {
                // Try to go back in history, fallback to search
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/search');
                }
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">
              {isLandlordView ? 'Back to properties' : 'Back to results'}
            </span>
          </button>
          <div className="flex items-center gap-2">
            {!canManage && (
              <button
                onClick={handleSave}
                className={cn(
                  'p-2.5 rounded-full border transition-all',
                  isSaved 
                    ? 'bg-red-500 border-red-500 text-white'
                    : 'border-gray-300 text-gray-600 hover:border-red-500 hover:text-red-500'
                )}
                aria-label={isSaved ? 'Remove from saved' : 'Save property'}
              >
                <Heart className={cn('w-5 h-5', isSaved && 'fill-current')} />
              </button>
            )}
            <button
              onClick={handleShare}
              className="p-2.5 rounded-full border border-gray-300 text-gray-600 hover:border-gray-400 transition-colors"
              aria-label="Share property"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Landlord Management Banner */}
      {canManage && (
        <div className="bg-emerald-600 text-white">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5" />
              <span className="font-medium">Property Management View</span>
              <span className="text-emerald-200 text-sm hidden sm:inline">
                - This is how tenants will see your listing
              </span>
            </div>
            <button
              onClick={() => window.open(`/properties/${actualId}`, '_blank')}
              className="text-sm px-3 py-1 bg-white/20 rounded hover:bg-white/30 transition-colors flex items-center gap-1"
            >
              <EyeOff className="w-4 h-4" />
              <span className="hidden sm:inline">Preview as Tenant</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Image Gallery */}
        <section className="mb-6">
          <div className="relative rounded-xl overflow-hidden bg-gray-100">
            {/* Main Image */}
            <div 
              className="aspect-[16/9] md:aspect-[2/1] cursor-pointer relative group"
              onClick={() => setIsGalleryOpen(true)}
            >
              <img
                src={images[currentImageIndex]}
                alt={`Property image ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-4 py-2 rounded-lg flex items-center gap-2">
                  <Expand className="w-5 h-5" />
                  <span className="font-medium">View all {images.length} photos</span>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Image Counter */}
            <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black/70 rounded-lg text-white text-sm flex items-center gap-2">
              <Grid className="w-4 h-4" />
              {currentImageIndex + 1} / {images.length}
            </div>
          </div>

          {/* Thumbnail Strip */}
          {images.length > 1 && (
            <div className="flex gap-2 mt-2 overflow-x-auto pb-2" ref={galleryRef}>
              {images.slice(0, 6).map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={cn(
                    'flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors',
                    currentImageIndex === idx ? 'border-emerald-500' : 'border-transparent hover:border-gray-300'
                  )}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {images.length > 6 && (
                <button
                  onClick={() => setIsGalleryOpen(true)}
                  className="flex-shrink-0 w-20 h-14 rounded-lg bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium hover:bg-gray-300 transition-colors"
                >
                  +{images.length - 6} more
                </button>
              )}
            </div>
          )}
        </section>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Info Column */}
          <div className="md:col-span-2 space-y-6">
            {/* Header */}
            <section>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {formatPrice(property?.rent_amount, property?.rent_frequency)}
              </p>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                {property?.listing_title || `${property?.bedrooms} Bedroom ${property?.property_type?.charAt(0).toUpperCase() + property?.property_type?.slice(1)}`}
              </h1>
              <div className="flex items-center gap-1.5 text-gray-600">
                <MapPin className="w-5 h-5" />
                <span>{displayAddress}</span>
              </div>
              <p className="text-gray-500 text-sm">{locationLine}</p>
            </section>

            {/* Key Features */}
            <section className="flex flex-wrap gap-4 py-4 border-y border-gray-200">
              {property?.bedrooms !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Bed className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Bedrooms</p>
                    <p className="font-semibold text-gray-900">{property.bedrooms}</p>
                  </div>
                </div>
              )}
              {property?.bathrooms !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Bath className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Bathrooms</p>
                    <p className="font-semibold text-gray-900">{property.bathrooms}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Sofa className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Furnishing</p>
                  <p className="font-semibold text-gray-900">
                    {property?.furnished ? 'Furnished' : 'Unfurnished'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Building className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Property Type</p>
                  <p className="font-semibold text-gray-900 capitalize">
                    {property?.property_type || 'Property'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Available</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(property?.available_from)}
                  </p>
                </div>
              </div>
            </section>

            {/* Description */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
              <div className={cn('text-gray-600 leading-relaxed', !showDescription && 'line-clamp-4')}>
                {property?.listing_description || property?.description || 'No description available for this property.'}
              </div>
              {(property?.listing_description?.length > 300 || property?.description?.length > 300) && (
                <button
                  onClick={() => setShowDescription(!showDescription)}
                  className="mt-2 text-emerald-600 font-medium hover:text-emerald-700"
                >
                  {showDescription ? 'Show less' : 'Read more'}
                </button>
              )}
            </section>

            {/* Key Features List */}
            {property?.features?.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h2>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {property.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-600">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Policies */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Property Policies</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={cn(
                    'w-3 h-3 rounded-full',
                    property?.pets_allowed ? 'bg-green-500' : 'bg-red-500'
                  )} />
                  <span className="text-gray-700">
                    {property?.pets_allowed ? 'Pets allowed' : 'No pets'}
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={cn(
                    'w-3 h-3 rounded-full',
                    property?.smoking_allowed ? 'bg-green-500' : 'bg-red-500'
                  )} />
                  <span className="text-gray-700">
                    {property?.smoking_allowed ? 'Smoking allowed' : 'No smoking'}
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={cn(
                    'w-3 h-3 rounded-full',
                    property?.bills_included ? 'bg-green-500' : 'bg-gray-400'
                  )} />
                  <span className="text-gray-700">
                    {property?.bills_included ? 'Bills included' : 'Bills not included'}
                  </span>
                </div>
                {property?.min_tenancy_months && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <span className="text-gray-700">
                      Min. {property.min_tenancy_months} months tenancy
                    </span>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Sidebar - Costs & CTA */}
          <aside className="space-y-6">
            {/* Costs Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Costs</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Rent</span>
                  <span className="font-semibold text-gray-900">
                    {formatPrice(property?.rent_amount, property?.rent_frequency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Deposit</span>
                  <span className="font-semibold text-gray-900">
                    {property?.deposit_amount 
                      ? `£${property.deposit_amount.toLocaleString()}`
                      : 'Contact agent'}
                  </span>
                </div>
                {property?.holding_deposit && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Holding Deposit</span>
                    <span className="font-semibold text-gray-900">
                      £{property.holding_deposit.toLocaleString()}
                    </span>
                  </div>
                )}
                {property?.council_tax_band && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Council Tax Band</span>
                    <span className="font-semibold text-gray-900">{property.council_tax_band}</span>
                  </div>
                )}
              </div>
            </div>

            {/* CTA Card - Hidden on mobile (shown as sticky bar) */}
            <div className="hidden md:block bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
              {canManage ? (
                /* Landlord Management Actions */
                <>
                  <button
                    onClick={() => navigate(`/landlord/properties/${actualId}/edit`)}
                    className="w-full py-3 px-4 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Edit className="w-5 h-5" />
                    Edit Property
                  </button>
                  <button
                    onClick={() => navigate(`/landlord/properties/${actualId}/rooms`)}
                    className="w-full py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Home className="w-5 h-5" />
                    Manage Rooms
                  </button>
                  <button
                    onClick={() => navigate(`/landlord/properties/${actualId}/hmo-compliance`)}
                    className="w-full py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <FileCheck className="w-5 h-5" />
                    Compliance
                  </button>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Status</span>
                      <span className={cn(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        property?.status === 'listed' ? 'bg-green-100 text-green-700' :
                        property?.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                        'bg-amber-100 text-amber-700'
                      )}>
                        {property?.status?.charAt(0).toUpperCase() + property?.status?.slice(1) || 'Draft'}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                /* Public/Tenant Actions */
                <>
                  <button
                    onClick={() => setShowViewing(true)}
                    className="w-full py-3 px-4 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Book Viewing
                  </button>
                  <button
                    onClick={() => setShowEnquiry(true)}
                    className="w-full py-3 px-4 border border-emerald-600 text-emerald-600 font-medium rounded-lg hover:bg-emerald-50 transition-colors"
                  >
                    Send Enquiry
                  </button>
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-500 text-center">
                      Landlord typically responds within 24 hours
                    </p>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile Sticky Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 md:hidden z-30">
        {canManage ? (
          /* Landlord Mobile Actions */
          <>
            <button
              onClick={() => navigate(`/landlord/properties/${actualId}/edit`)}
              className="flex-1 py-3 px-4 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
            >
              <Edit className="w-5 h-5" />
              Edit
            </button>
            <button
              onClick={() => navigate(`/landlord/properties/${actualId}/rooms`)}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <Settings className="w-5 h-5" />
              Manage
            </button>
          </>
        ) : (
          /* Public Mobile Actions */
          <>
            <button
              onClick={() => setShowViewing(true)}
              className="flex-1 py-3 px-4 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Book Viewing
            </button>
            <button
              onClick={() => setShowEnquiry(true)}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Enquire
            </button>
          </>
        )}
      </div>

      {/* Full Screen Gallery */}
      {isGalleryOpen && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          {/* Gallery Header */}
          <div className="flex items-center justify-between p-4 text-white">
            <span className="text-lg font-medium">
              {currentImageIndex + 1} of {images.length}
            </span>
            <button
              onClick={() => setIsGalleryOpen(false)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close gallery"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Gallery Image */}
          <div className="flex-1 flex items-center justify-center p-4">
            <img
              src={images[currentImageIndex]}
              alt={`Property image ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Gallery Navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </>
          )}

          {/* Thumbnail Strip */}
          <div className="p-4 flex justify-center gap-2 overflow-x-auto">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={cn(
                  'flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-colors',
                  currentImageIndex === idx ? 'border-white' : 'border-transparent opacity-50 hover:opacity-75'
                )}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dialogs */}
      <EnquiryDialog
        isOpen={showEnquiry}
        onClose={() => setShowEnquiry(false)}
        property={property}
        user={user}
      />

      <ViewingRequestDialog
        isOpen={showViewing}
        onClose={() => setShowViewing(false)}
        property={property}
        user={user}
      />
    </div>
  );
};

export default PropertyDetailV2;
